import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import type { PatientSubsectionDescriptor } from '@/features/patient-record/sections.config';

import { useSubsectionContext } from './SubsectionContext';

interface PatientSubTabsProps {
  readonly subsections: readonly PatientSubsectionDescriptor[];
}

/**
 * V1 sub-tab strip — a horizontal jump-and-open navigator. A tab's label
 * is rendered in foreground weight when its card is open and muted when
 * closed, so the strip doubles as a "what's expanded" overview. Clicking
 * a tab opens the matching card (if closed) and scrolls it into view;
 * other open cards are left alone, so the user can keep several panels
 * visible at once.
 *
 * Renders `null` when the section has zero or one subsection — V1 spec:
 * never render an empty bar.
 */
export function PatientSubTabs({
  subsections,
}: PatientSubTabsProps): ReactElement | null {
  const { t } = useTranslation();
  const ctx = useSubsectionContext();

  if (subsections.length <= 1) return null;

  const handleClick = (id: string): void => {
    if (ctx === null) return;
    ctx.setOpen(id, true);
    ctx.scrollTo(id);
  };

  return (
    <nav
      aria-label={t('patient.subtabs.ariaLabel')}
      className="bg-card border-b border-border px-6 overflow-x-auto shrink-0"
    >
      <div className="flex items-stretch gap-1 -mb-px">
        {subsections.map((sub) => {
          const isOpen = ctx?.openIds.has(sub.id) ?? false;
          return (
            <button
              key={sub.id}
              type="button"
              onClick={() => {
                handleClick(sub.id);
              }}
              aria-pressed={isOpen}
              className={cn(
                'inline-flex items-center whitespace-nowrap',
                'px-3 py-2.5 text-[13px]',
                'border-b-2 border-transparent transition-colors',
                isOpen
                  ? 'text-foreground font-semibold hover:text-brand'
                  : 'text-muted-foreground font-medium hover:text-foreground',
              )}
            >
              {t(sub.labelKey)}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
